import { PageLayout } from '../../layout/PageLayout'
import { RegionalPageLayout } from '../../layout/RegionalPageLayout'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { MapPageHeader } from '@app/components/blocks/pageHeader/MapPageHeader'
import {
  faPlus,
  faCheck,
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons'
import { navigate } from 'wouter/use-browser-location'
import { pages } from '@app/util/links'
import { RegionViewModel } from 'functions/app/geography/view/region'
import { useAuthState, useClient, useQuery } from '@based/sdk/react'
import { enrich } from '@i18n/localizeHelper'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { LockedPageCover } from '@app/components/blocks/lockedPageCover/LockedPageCover'
import useMediaQuery from '@app/util/useMediaQuery'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { getRegionPageTitle } from './getPageTitle'
import { shouldNoindexPageTab } from '@app/util/seo/shouldNoindexPageTab'
import {
  parseRegionPageTab,
  regionPageTabToUrlSegment,
  resolveRegionPageTab,
} from './parseRegionPageTab'
import { RegionalPageHeader } from '@app/components/blocks/pageHeader/RegionalPageHeader'
import { RegionalHeaderScrollSentinel } from './regionalPage/RegionalHeaderScrollContext'
import { ContentSections } from '@app/components/blocks/contentSection/ContentSection'
import {
  mapPageContentBlocksToSections,
  PAGE_CONTENT_INITIATIVES_LIMIT,
} from '@app/components/blocks/contentSection/mapPageContentBlocksToSections'
import { ThreadLatestViewModel } from 'functions/app/thread/view-latest'
import { RegionalPageTabContent } from './regionalPage/RegionalPageTabContent'
import { regionTabs } from './constants'
import type { RegionFeatures } from '@schema/featureToggle'

export type RegionPageProps = {
  slug: string
  tab?: string
}

export const RegionPage = ({ slug, tab: tabParam }: RegionPageProps) => {
  const isMobile = useMediaQuery()
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const translations = useTranslations().app
  const regionQuery = useQuery('view-region', { slug })
  const { withAuthCheck } = useAuthModal()
  const client = useClient()
  const authState = useAuthState()

  useEffect(() => {
    setMounted(true)
  }, [])

  const tab = resolveRegionPageTab(parseRegionPageTab(tabParam))

  useEffect(() => {
    if (!tabParam) {
      return
    }

    const canonicalSegment =
      tab === regionTabs.overview ? undefined : regionPageTabToUrlSegment(tab)

    if (!canonicalSegment) {
      navigate(pages.regionPageNew(slug))
      return
    }

    if (tabParam !== canonicalSegment) {
      navigate(pages.regionPageNew(slug, tab))
    }
  }, [tabParam, tab, slug])

  const region: RegionViewModel = regionQuery.data

  const discussionsQuery = useQuery('view-latest-thread', {
    id: region?.thread?.id,
  })
  const discussions: ThreadLatestViewModel = discussionsQuery.data ?? {
    posts: [],
  }

  const initiativeQuery = useQuery('list-regional-initiative', {
    regionId: region?.id,
    top: PAGE_CONTENT_INITIATIVES_LIMIT,
  })

  const overviewStoriesQuery = useQuery(
    region?.id ? 'list-regional-overview-stories' : null,
    { regionId: region?.id },
  )
  const overviewNewsQuery = useQuery(
    region?.id ? 'list-regional-overview-news' : null,
    { regionId: region?.id },
  )

  const contentSections = useMemo(
    () =>
      mapPageContentBlocksToSections(region?.contentBlocks, {
        stories: overviewStoriesQuery.data ?? [],
        news: overviewNewsQuery.data ?? [],
        threadPosts: discussions.posts ?? [],
        initiatives: initiativeQuery.data ?? [],
        titleLevel: 'H2',
      }),
    [
      region?.contentBlocks,
      overviewStoriesQuery.data,
      overviewNewsQuery.data,
      discussions.posts,
      initiativeQuery.data,
    ],
  )

  const pageManager = region?.pageManager
  const isHomeTab = tab === regionTabs.overview

  const followerCountQuery = useQuery('count-followers', {
    entityType: 'region',
    entityIds: region?.id ? [region?.id] : [],
  })
  const followerCount = (followerCountQuery.data as number[])?.[0] ?? 0

  const followRegion = useCallback(() => {
    if (!region?.id) return

    void withAuthCheck(async () => {
      await client.call('follow-region', {
        regionId: region.id,
      })
    })
  }, [withAuthCheck, client, region?.id])

  const isFollowingQuery = useQuery(authState?.userId ? 'is-following' : null, {
    entityType: 'region',
    entityId: region?.id,
    userId: Number(authState?.userId),
  })
  const isFollowing = mounted && ((isFollowingQuery.data as boolean) ?? false)
  // Use stable icon and label until after mount to avoid hydration mismatch (auth state differs server vs client)
  const followButtonIcon = mounted ? (isFollowing ? faCheck : faPlus) : faPlus
  const followButtonLabel = mounted
    ? isFollowing
      ? translations.common.following
      : translations.common.follow
    : translations.common.follow

  const mapHeaderButtons = useMemo(
    () => [
      {
        label: followButtonLabel,
        onClick: followRegion,
        variant: 'primary' as const,
        size: isMobile ? ('small' as const) : ('medium' as const),
        icon: followButtonIcon,
        iconPosition: 'left' as const,
      },
      {
        label: translations.common.share,
        onClick: () => setIsShareModalOpen(true),
        variant: 'secondary' as const,
        size: isMobile ? ('small' as const) : ('medium' as const),
      },
    ],
    [
      translations.common.share,
      isMobile,
      followButtonLabel,
      followRegion,
      followButtonIcon,
    ],
  )

  const regionPageStatus = getEntityPageStatus(regionQuery.loading, region)

  if (regionPageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (regionPageStatus === 'loading') {
    return (
      <>
        <PageLayout gap="2rem">
          <PageSeo />
          <Row style={{ alignItems: 'flex-start' }} gap="3rem">
            <Column
              gap="1.5rem"
              alignItems="stretch"
              style={{ flex: '2 1 auto' }}
            >
              <MapPageHeader title={translations.common.loading} />
            </Column>
          </Row>
        </PageLayout>
      </>
    )
  }

  return (
    <>
      <RegionalPageLayout region={region} tab={tab}>
        <PageHead region={region} section={tab} />
        {isHomeTab ? (
          <Column gap={isMobile ? '2rem' : '5.5rem'} alignItems="stretch">
            <RegionalPageHeader
              title={region.name}
              subtitle={region.slogan}
              banner={region.backgroundImage}
              buttons={mapHeaderButtons}
              hasBadge={region.crowdbuildingPartner}
              profileRole={translations.regionPage.managedBy}
              profile={pageManager}
              followers={region.followers}
              totalFollowers={followerCount}
            />
            <RegionalHeaderScrollSentinel />
            <ContentSections sections={contentSections} />
          </Column>
        ) : (
          <RegionalPageTabContent region={region} regionSlug={slug} tab={tab} />
        )}
      </RegionalPageLayout>

      <SocialShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={region.name}
      />

      {region.crowdbuildingPartner === false &&
        (isFollowing ? (
          <LockedPageCover
            title={translations.regionPage.lockedProvinceFollowed}
            body={translations.regionPage.lockedProvinceBodyFollowed}
            buttonProps={[
              {
                variant: 'secondary',
                size: 'medium',
                label: translations.common.back,
                onClick: () => navigate(pages.regions),
                icon: faArrowLeft,
                iconPosition: 'left',
              },
              {
                variant: 'primary',
                size: 'medium',
                label: translations.regionPage.pageFollowed,
                onClick: followRegion,
                icon: faCheck,
                iconPosition: 'left',
              },
            ]}
            style={{
              inset: 'var(--header-height) 0 0 0',
            }}
          />
        ) : (
          <LockedPageCover
            title={enrich(
              translations.regionPage.lockedProvinceHeader,
              region.name,
            )}
            body={enrich(
              translations.regionPage.lockedProvinceBody,
              region.name,
            )}
            buttonProps={[
              {
                variant: 'primary',
                size: 'medium',
                label: translations.regionPage.followThePage,
                onClick: followRegion,
              },
            ]}
            style={{
              inset: 'var(--header-height) 0 0 0',
            }}
          />
        ))}
    </>
  )
}

const PageHead = ({
  region,
  section,
}: {
  region: RegionViewModel
  section: RegionFeatures
}) => {
  const translations = useTranslations().app.regionPage
  const defaultPageTitle = getRegionPageTitle(
    section,
    region.name,
    translations.pageTabTitle,
  )
  return (
    <PageSeo
      pageTitle={defaultPageTitle}
      description={region.content}
      meta={{
        metaTitle: region.metaTitle,
        metaDescription: region.metaDescription,
      }}
      image={region.image}
      noindex={shouldNoindexPageTab(section)}
    />
  )
}
